import type { DifficultyLevel, ReadingMode } from '@lilith-arcana-os/domain';

import type { SpreadDefinition } from '../definitions/core';
import { ALL_SPREAD_DEFINITIONS } from './all-spreads';
import { QUESTION_INTENT_PROFILE_MAP } from '../roles/question-intents';
import type { SpreadIntent } from '../roles/position-roles';

export interface SpreadRecommendation {
  spread_slug: string;
  reason: string;
  score: number;
}

const levelWeight = (
  spreadLevel: DifficultyLevel,
  userLevel: DifficultyLevel | null,
): { score: number; reason: string | null } => {
  if (!userLevel) {
    return { score: 0, reason: null };
  }

  const matrix: Record<DifficultyLevel, Record<DifficultyLevel, number>> = {
    beginner: { beginner: 18, intermediate: 8, advanced: -10, initiatic: -18 },
    intermediate: { beginner: -2, intermediate: 15, advanced: 8, initiatic: -4 },
    advanced: { beginner: -8, intermediate: 6, advanced: 14, initiatic: 8 },
    initiatic: { beginner: -12, intermediate: 2, advanced: 10, initiatic: 16 },
  };

  const score = matrix[userLevel][spreadLevel];
  return {
    score,
    reason: score >= 10 ? 'difficoltà coerente' : score < 0 ? 'difficoltà potenzialmente alta' : null,
  };
};

const modeWeight = (
  definition: SpreadDefinition,
  mode: ReadingMode | null,
): { score: number; reason: string | null } => {
  if (!mode) {
    return { score: 0, reason: null };
  }

  if (mode === 'lilith') {
    if (definition.spread.category === 'lilith') {
      return { score: 28, reason: 'allineato al mode lilith' };
    }

    if (definition.intent.includes('shadow') || definition.intent.includes('lilith')) {
      return { score: 12, reason: 'compatibile con ombra/desiderio' };
    }

    return { score: -6, reason: null };
  }

  if (mode === 'qliphotic') {
    if (definition.intent.includes('shadow') || definition.spread.category === 'lilith') {
      return { score: 16, reason: 'utile per lettura ombra/qliphotic' };
    }

    return { score: -4, reason: null };
  }

  if (mode === 'kabbalistic') {
    if (definition.spread.category === 'kabbalistic') {
      return { score: 24, reason: 'allineato al mode kabbalistico' };
    }

    if (definition.spread.tree_of_life_mapping) {
      return { score: 12, reason: 'mappa già il linguaggio sephirotico' };
    }

    return { score: -4, reason: null };
  }

  return { score: 0, reason: null };
};

export function recommendSpreads(input: {
  intent?: SpreadIntent | null;
  desiredCards?: number | null;
  ritual?: boolean | null;
  userLevel?: DifficultyLevel | null;
  mode?: ReadingMode | null;
}): SpreadRecommendation[] {
  const intentProfile = input.intent ? QUESTION_INTENT_PROFILE_MAP[input.intent] : null;

  return ALL_SPREAD_DEFINITIONS.map<SpreadRecommendation>((definition) => {
    let score = 0;
    const reasons: string[] = [];

    if (input.intent) {
      if (definition.intent.includes(input.intent)) {
        score += 36;
        reasons.push('intent match');
      } else if (intentProfile?.recommended_spread_slugs.includes(definition.spread.slug)) {
        score += 24;
        reasons.push('raccomandato per l’intento');
      } else if (intentProfile?.fallback_spread_slugs.includes(definition.spread.slug)) {
        score += 12;
        reasons.push('fallback utile');
      }
    }

    if (input.desiredCards !== null && input.desiredCards !== undefined) {
      const distance = Math.abs(definition.spread.cards - input.desiredCards);
      const cardScore = Math.max(0, 18 - distance * 4);
      score += cardScore;

      if (cardScore >= 10) {
        reasons.push('numero carte vicino alla richiesta');
      }
    }

    if (input.ritual === true) {
      const ritualScore = definition.ritual_intensity * 3 + (definition.spread.ritual_note ? 4 : 0);
      score += ritualScore;

      if (ritualScore >= 10) {
        reasons.push('buona intensità rituale');
      }
    }

    if (input.ritual === false && definition.ritual_intensity >= 4) {
      score -= 6;
    }

    const difficulty = levelWeight(definition.spread.difficulty, input.userLevel ?? null);
    score += difficulty.score;
    if (difficulty.reason) {
      reasons.push(difficulty.reason);
    }

    const mode = modeWeight(definition, input.mode ?? null);
    score += mode.score;
    if (mode.reason) {
      reasons.push(mode.reason);
    }

    if (definition.spread.category === 'core') {
      score += 4;
    }

    return {
      spread_slug: definition.spread.slug,
      reason: reasons.length > 0 ? reasons.slice(0, 3).join('; ') : 'coerenza generale del registry',
      score,
    };
  }).sort((left, right) => right.score - left.score || left.spread_slug.localeCompare(right.spread_slug));
}
